import { useState } from "react";
import { Plus, Trash2, Check, X } from "lucide-react";

export interface IncludedItem {
  text: string;
  textSk?: string;
}

interface Props {
  included: IncludedItem[];
  notIncluded: IncludedItem[];
  onChangeIncluded: (value: IncludedItem[]) => void;
  onChangeNotIncluded: (value: IncludedItem[]) => void;
}

export default function IncludedListEditor({
  included,
  notIncluded,
  onChangeIncluded,
  onChangeNotIncluded,
}: Props) {
  const [newIncluded, setNewIncluded] = useState("");
  const [newNotIncluded, setNewNotIncluded] = useState("");

  const addIncluded = () => {
    if (!newIncluded.trim()) return;
    onChangeIncluded([...included, { text: newIncluded.trim() }]);
    setNewIncluded("");
  };

  const addNotIncluded = () => {
    if (!newNotIncluded.trim()) return;
    onChangeNotIncluded([...notIncluded, { text: newNotIncluded.trim() }]);
    setNewNotIncluded("");
  };

  const updateIncluded = (index: number, updates: Partial<IncludedItem>) => {
    const newValue = [...included];
    newValue[index] = { ...newValue[index], ...updates };
    onChangeIncluded(newValue);
  };

  const updateNotIncluded = (index: number, updates: Partial<IncludedItem>) => {
    const newValue = [...notIncluded];
    newValue[index] = { ...newValue[index], ...updates };
    onChangeNotIncluded(newValue);
  };

  const removeIncluded = (index: number) => {
    onChangeIncluded(included.filter((_, i) => i !== index));
  };

  const removeNotIncluded = (index: number) => {
    onChangeNotIncluded(notIncluded.filter((_, i) => i !== index));
  };

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      {/* Included */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-success-100 dark:bg-success-900/30">
            <Check className="h-4 w-4 text-success-600 dark:text-success-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Cena zahrnuje ({included.length})
          </h3>
        </div>

        {/* Add new */}
        <div className="flex gap-2">
          <input
            type="text"
            value={newIncluded}
            onChange={(e) => setNewIncluded(e.target.value)}
            placeholder="např. Letenky v obou směrech"
            className="input flex-1"
            onKeyDown={(e) =>
              e.key === "Enter" && (e.preventDefault(), addIncluded())
            }
          />
          <button
            type="button"
            onClick={addIncluded}
            disabled={!newIncluded.trim()}
            className="btn-primary btn-sm"
          >
            <Plus className="h-4 w-4" />
          </button>
        </div>

        {/* List */}
        <div className="space-y-2">
          {included.map((item, index) => (
            <div
              key={index}
              className="rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800"
            >
              <div className="flex items-start gap-2">
                <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-success-500" />
                <div className="flex-1 space-y-2">
                  <input
                    type="text"
                    value={item.text}
                    onChange={(e) => updateIncluded(index, { text: e.target.value })}
                    placeholder="Text (CZ)"
                    className="input input-sm"
                  />
                  <input
                    type="text"
                    value={item.textSk || ""}
                    onChange={(e) => updateIncluded(index, { textSk: e.target.value })}
                    placeholder="Text (SK) - volitelné"
                    className="input input-sm text-gray-500"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => removeIncluded(index)}
                  className="p-1 text-error-500 hover:text-error-600"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}

          {included.length === 0 && (
            <div className="rounded-lg border-2 border-dashed border-gray-300 p-4 text-center text-sm text-gray-500 dark:border-gray-600">
              Zatím žádné položky
            </div>
          )}
        </div>
      </div>

      {/* Not Included */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-error-100 dark:bg-error-900/30">
            <X className="h-4 w-4 text-error-600 dark:text-error-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Cena nezahrnuje ({notIncluded.length})
          </h3>
        </div>

        {/* Add new */}
        <div className="flex gap-2">
          <input
            type="text"
            value={newNotIncluded}
            onChange={(e) => setNewNotIncluded(e.target.value)}
            placeholder="např. Vstupní víza"
            className="input flex-1"
            onKeyDown={(e) =>
              e.key === "Enter" && (e.preventDefault(), addNotIncluded())
            }
          />
          <button
            type="button"
            onClick={addNotIncluded}
            disabled={!newNotIncluded.trim()}
            className="btn-primary btn-sm"
          >
            <Plus className="h-4 w-4" />
          </button>
        </div>

        {/* List */}
        <div className="space-y-2">
          {notIncluded.map((item, index) => (
            <div
              key={index}
              className="rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800"
            >
              <div className="flex items-start gap-2">
                <X className="mt-0.5 h-4 w-4 flex-shrink-0 text-error-500" />
                <div className="flex-1 space-y-2">
                  <input
                    type="text"
                    value={item.text}
                    onChange={(e) => updateNotIncluded(index, { text: e.target.value })}
                    placeholder="Text (CZ)"
                    className="input input-sm"
                  />
                  <input
                    type="text"
                    value={item.textSk || ""}
                    onChange={(e) => updateNotIncluded(index, { textSk: e.target.value })}
                    placeholder="Text (SK) - volitelné"
                    className="input input-sm text-gray-500"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => removeNotIncluded(index)}
                  className="p-1 text-error-500 hover:text-error-600"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}

          {notIncluded.length === 0 && (
            <div className="rounded-lg border-2 border-dashed border-gray-300 p-4 text-center text-sm text-gray-500 dark:border-gray-600">
              Zatím žádné položky
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
